import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Fingerprint, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(phone.trim(), password);
      toast.success(`Selamat datang, ${user.name}`);
      navigate(user.role === "admin" ? "/admin" : user.role === "parent" ? "/parent" : "/pos");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden" style={{ backgroundColor: "#064e3b" }}>
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cg fill='none' stroke='%236ee7b7' stroke-width='1.2'%3E%3Cpath d='M40 0l40 40-40 40L0 40z'/%3E%3Cpath d='M40 14l26 26-26 26-26-26z'/%3E%3Crect x='30' y='30' width='20' height='20' transform='rotate(45 40 40)'/%3E%3Ccircle cx='40' cy='40' r='5'/%3E%3C/g%3E%3C/svg%3E\")",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/60 via-transparent to-emerald-950/80" />
        <div className="relative flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="w-16 h-16 object-contain drop-shadow" />
          <div>
            <p className="text-white font-heading font-bold text-lg leading-tight">Pondok Pesantren</p>
            <p className="text-emerald-400 font-heading font-bold text-2xl leading-tight">AL-KHOIR</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
            SISTEM INFORMASI KEUANGAN
          </h1>
          <p className="text-emerald-100/90 mt-4 max-w-md leading-relaxed">
            Modernisasi pengelolaan Keuangan Pondok Pesantren, upaya mewujudkan pengelolaan yang
            Amanah dan Mudah.
          </p>
        </div>
        <p className="relative text-emerald-200/70 text-sm">© 2026 Ponpes Al-Khoir</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-stone-50">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="" className="w-12 h-12 object-contain" />
            <p className="font-heading font-bold text-xl text-stone-900">Al-Khoir</p>
          </div>
          <h2 className="font-heading text-3xl font-bold text-stone-900">Masuk Akun</h2>
          <p className="text-stone-500 mt-2">Gunakan Nomor HP dan password Anda.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="phone">Nomor HP</Label>
              <Input
                id="phone"
                data-testid="login-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="08xxxxxxxxxx"
                className="rounded-xl py-6"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-xl py-6"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-base font-semibold"
            >
              {loading ? <Loader2 className="animate-spin" /> : "Masuk"}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-2 text-xs text-stone-400">
            <Fingerprint size={16} className="text-emerald-600" />
            Login sidik jari akan tersedia pada aplikasi mobile.
          </div>

          <div className="mt-8 pt-6 border-t border-stone-200" data-testid="login-legal-links">
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-stone-500">
              <Link to="/syarat-ketentuan" className="hover:text-emerald-700 transition-colors" data-testid="link-terms">
                Syarat &amp; Ketentuan
              </Link>
              <Link to="/kebijakan-refund" className="hover:text-emerald-700 transition-colors" data-testid="link-refund">
                Kebijakan Refund
              </Link>
              <Link to="/faq" className="hover:text-emerald-700 transition-colors" data-testid="link-faq">
                FAQ
              </Link>
              <Link to="/kontak" className="hover:text-emerald-700 transition-colors" data-testid="link-contact">
                Kontak
              </Link>
            </div>
            <p className="text-[11px] text-stone-400 mt-3">© 2026 Pondok Pesantren Al-Khoir</p>
          </div>
        </div>
      </div>
    </div>
  );
}
